import { useMutation, useQueryClient, type QueryClient } from '@tanstack/react-query';
import type { LoginInput, RegisterInput, UpdatePreferencesInput } from 'shared';

import { login, logout, register, updatePreferences } from '../../lib/api/endpoints';
import { tokenStore } from '../../lib/api/tokenStore';
import { useUiStore } from '../../stores/uiStore';

import { authKeys } from './queries';

function forgetPreviousSession(queryClient: QueryClient): void {
  useUiStore.getState().setLastPrimaryAccount(null);
  queryClient.removeQueries();
}

export function useLogin() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: LoginInput) => login(input),
    onSuccess: (result) => {
      tokenStore.set(result.token);
      forgetPreviousSession(queryClient);
      queryClient.setQueryData(authKeys.me, result.user);
      void queryClient.invalidateQueries({ queryKey: authKeys.me });
    },
  });
}

export function useRegister() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: RegisterInput) => register(input),
    onSuccess: (result) => {
      tokenStore.set(result.token);
      forgetPreviousSession(queryClient);
      queryClient.setQueryData(authKeys.me, result.user);
      void queryClient.invalidateQueries({ queryKey: authKeys.me });
    },
  });
}

export function useLogout() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: logout,
    onSuccess: () => {
      tokenStore.clear();
      forgetPreviousSession(queryClient);
    },
  });
}

export function useUpdatePreferences() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdatePreferencesInput) => updatePreferences(input),
    onSuccess: (user) => {
      queryClient.setQueryData(authKeys.me, user);
      void queryClient.invalidateQueries({ queryKey: authKeys.me });
    },
  });
}
